// Blog.jsx - Main blog page component with full CRUD functionality
// Features: Real-time search, tag filtering, post management, media uploads

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Calendar, User, Search, X, Plus, MoreVertical } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import config from '../config';
import Portal from '../components/Portal';
import '../styles/Page.css';
import '../styles/Blog.css';

const hostURL = config.serverUrl;

function Blog() {
    // ========== Authentication & User State ==========
    const { currentUser } = useAuth();
    const isAdminOrOwner = currentUser && (currentUser.access === 'admin' || currentUser.access === 'owner');

    // ========== Data State ==========
    const [posts, setPosts] = useState([]);              // All blog posts
    const [tags, setTags] = useState([]);                // All available tags
    const [selectedTags, setSelectedTags] = useState([]); // Currently filtered tags
    const [searchTerm, setSearchTerm] = useState('');    // Search input value
    const [loading, setLoading] = useState(true);        // Loading state for posts
    const [error, setError] = useState('');              // Error message display
    
    // ========== Popup Visibility States ==========
    const [showAddPostPopup, setShowAddPostPopup] = useState(false);     // Add/Edit post form
    const [showAddTagPopup, setShowAddTagPopup] = useState(false);       // Add tag form
    const [showDeleteTagPopup, setShowDeleteTagPopup] = useState(false); // Delete tag popup
    const [showReadMorePopup, setShowReadMorePopup] = useState(false);   // Full post view
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);   // Delete confirmation
    const [selectedPost, setSelectedPost] = useState(null);              // Post being viewed/edited
    const [postToDelete, setPostToDelete] = useState(null);              // Post pending deletion
    const [tagToDelete, setTagToDelete] = useState('');                  // Tag ID selected for deletion
    const [showTagDeleteConfirm, setShowTagDeleteConfirm] = useState(false); // Tag delete confirmation
    const [dropdownOpen, setDropdownOpen] = useState(null);              // Three-dot menu state
    const [showAllTagsModal, setShowAllTagsModal] = useState(false);    // All tags modal visibility
    const [numColumns, setNumColumns] = useState(window.innerWidth >= 1200 ? 3 : 2); // Number of columns for masonry layout
    
    // ========== Form States ==========
    // Post form data (used for both create and edit)
    const [postForm, setPostForm] = useState({
        title: '',
        author_name: '',
        content: '',
        media_type: 'image',  // Options: 'image', 'video', 'youtube', 'none'
        media_url: '',
        tag_ids: []
    });
    const [mediaFile, setMediaFile] = useState(null);    // File object for image/video upload
    const [isDragging, setIsDragging] = useState(false);  // Drag-over state for file drop
    const [tagForm, setTagForm] = useState({ label: '' }); // Tag creation form
    const [formError, setFormError] = useState('');       // Form validation errors
    const [submitting, setSubmitting] = useState(false);  // Form submission state
    const [isEditMode, setIsEditMode] = useState(false);  // Track if editing existing post

    // ========== Effects ==========
    // Initial data fetch on component mount
    useEffect(() => {
        fetchTags();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Real-time filtering: Re-fetch posts when search term or selected tags change
    useEffect(() => {
        fetchPosts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, selectedTags]);

    // Window resize listener for responsive column layout
    useEffect(() => {
        let rafId;
        const handleResize = () => {
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
            rafId = requestAnimationFrame(() => {
                const newNumColumns = window.innerWidth >= 1200 ? 3 : 2;
                if (newNumColumns !== numColumns) {
                    setNumColumns(newNumColumns);
                }
            });
        };

        window.addEventListener('resize', handleResize);
        return () => {
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
            window.removeEventListener('resize', handleResize);
        };
    }, [numColumns]);

    // ========== API Functions ==========
    /**
     * Fetch posts from the server with optional search and tag filters
     * Builds query parameters from searchTerm and selectedTags state
     */
    const fetchPosts = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (searchTerm) params.append('search', searchTerm);
            if (selectedTags.length > 0) params.append('tags', selectedTags.join(','));
            
            const response = await axios.get(`${hostURL}/api/posts?${params.toString()}`);
            setPosts(response.data.posts || []);
            setError('');
        } catch (err) {
            console.error('Error fetching posts:', err);
            setPosts([]);
            setError('Failed to load posts');
        } finally {
            setLoading(false);
        }
    }, [searchTerm, selectedTags]);

    /**
     * Fetch all available tags from the server
     */
    const fetchTags = useCallback(async () => {
        try {
            const response = await axios.get(`${hostURL}/api/tags`);
            setTags(response.data.tags || []);
        } catch (err) {
            console.error('Error fetching tags:', err);
            setTags([]);
        }
    }, []);

    /**
     * Get top 7 most common tags based on post count
     * Memoized to avoid recalculating on every render
     */
    const topTags = useMemo(() => {
        return [...tags]
            .sort((a, b) => (b.post_count || 0) - (a.post_count || 0))
            .slice(0, 7);
    }, [tags]);

    // ========== Event Handlers ==========
    /**
     * Toggle tag selection for filtering posts
     * Adds tag if not selected, removes if already selected
     */
    const handleTagToggle = useCallback((tagId) => {
        setSelectedTags(prev => 
            prev.includes(tagId) 
                ? prev.filter(id => id !== tagId)
                : [...prev, tagId]
        );
    }, []);

    /**
     * Open the Add Post popup with a fresh form
     */
    const handleAddPost = () => {
        setIsEditMode(false);
        setPostForm({
            title: '',
            author_name: '',
            content: '',
            media_type: 'image',
            media_url: '',
            tag_ids: []
        });
        setMediaFile(null);
        setFormError('');
        setShowAddPostPopup(true);
    };

    /**
     * Open the Edit Post popup with existing post data pre-filled
     */
    const handleEditPost = (post) => {
        setIsEditMode(true);
        setPostForm({
            title: post.title,
            author_name: post.author_name,
            content: post.content,
            media_type: post.media_type,
            media_url: post.media_url || '',
            tag_ids: post.tags.map(t => t.id)
        });
        setSelectedPost(post);
        setMediaFile(null);
        setFormError('');
        setShowReadMorePopup(false);
        setShowAddPostPopup(true);
    };

    /**
     * Delete the selected post after confirmation
     */
    const handleDeletePost = async () => {
        try {
            await axios.delete(`${hostURL}/api/posts/${postToDelete.id}`, { withCredentials: true });
            fetchPosts();
            setShowDeleteConfirm(false);
            setShowReadMorePopup(false);
            setPostToDelete(null);
        } catch (err) {
            console.error('Error deleting post:', err);
            setFormError('Failed to delete post');
        }
    };

    /**
     * Handle changes to post form inputs
     * Clears media file when media type changes
     */
    const handlePostFormChange = (e) => {
        const { name, value } = e.target;
        setPostForm(prev => ({ ...prev, [name]: value }));
        
        // Clear media file if media type changes
        if (name === 'media_type') {
            setMediaFile(null);
            setPostForm(prev => ({ ...prev, media_url: '' }));
        }
    };

    /**
     * Handle media file selection and validation
     * Validates file type based on selected media type (image or video)
     */
    const handleMediaFileChange = (e) => {
        setMediaFile(e.target.files[0]);
    };

    // Drag and drop handlers for image upload
    const handleDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Only set isDragging to false if we're leaving the drop zone entirely
        // Check if the related target is still a child of the current target
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setIsDragging(false);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        
        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const file = files[0];
            // Check if it's an image
            if (file.type.startsWith('image/')) {
                setMediaFile(file);
                setFormError('');
            } else {
                setFormError('Please drop an image file');
            }
        }
    };

    /**
     * Toggle tag selection in the post form
     */
    const handleTagCheckbox = (tagId) => {
        setPostForm(prev => ({
            ...prev,
            tag_ids: prev.tag_ids.includes(tagId)
                ? prev.tag_ids.filter(id => id !== tagId)
                : [...prev.tag_ids, tagId]
        }));
    };

    /**
     * Validate post form before submission
     * Returns error message string or null if valid
     * Matches database constraints for data integrity
     */
    const validatePostForm = () => {
        // Required field checks
        if (!postForm.title.trim()) return 'Title is required';
        if (!postForm.author_name.trim()) return 'Author name is required';
        if (!postForm.content.trim()) return 'Content is required';
        
        // Length constraints (matching database)
        if (postForm.title.length > 255) {
            return 'Title must be 255 characters or less';
        }
        if (postForm.author_name.length > 100) {
            return 'Author name must be 100 characters or less';
        }
        if (postForm.content.length > 10000) {
            return 'Content must be 10,000 characters or less';
        }
        
        // Media validation
        if (postForm.media_type === 'youtube') {
            if (!postForm.media_url.trim()) return 'YouTube URL is required';
            if (postForm.media_url.length > 2048) {
                return 'Media URL must be 2,048 characters or less';
            }
            // Accept both watch/share formats and embed format
            const youtubeRegex = /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]+)/;
            if (!youtubeRegex.test(postForm.media_url)) {
                return 'Invalid YouTube URL. Please use a valid YouTube link.';
            }
        } else if (postForm.media_type === 'image') {
            if (!isEditMode && !mediaFile) {
                return 'Please upload an image';
            }
        }
        
        return null;
    };

    /**
     * Submit post form (create new or update existing)
     * Handles media upload if file is provided
     * Converts YouTube URLs to embed format
     */
    const handleSubmitPost = async (e) => {
        e.preventDefault();
        
        const validationError = validatePostForm();
        if (validationError) {
            setFormError(validationError);
            return;
        }

        setSubmitting(true);
        setFormError('');

        try {
            let mediaUrl = postForm.media_url;

            // Upload media file if provided
            if (mediaFile && postForm.media_type !== 'youtube') {
                const formData = new FormData();
                formData.append('media', mediaFile);
                formData.append('mediaType', postForm.media_type);

                const uploadResponse = await axios.post(
                    `${hostURL}/api/blog/upload-media`,
                    formData,
                    {
                        withCredentials: true,
                        headers: { 'Content-Type': 'multipart/form-data' }
                    }
                );
                mediaUrl = uploadResponse.data.url;
            }

            // Extract YouTube video ID if YouTube URL
            if (postForm.media_type === 'youtube') {
                const youtubeRegex = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/;
                const match = postForm.media_url.match(youtubeRegex);
                if (match) {
                    mediaUrl = `https://www.youtube.com/embed/${match[1]}`;
                }
            }

            const postData = {
                ...postForm,
                media_url: postForm.media_type === 'none' ? null : mediaUrl
            };

            if (isEditMode) {
                await axios.put(
                    `${hostURL}/api/posts/${selectedPost.id}`,
                    postData,
                    { withCredentials: true }
                );
            } else {
                await axios.post(
                    `${hostURL}/api/posts`,
                    postData,
                    { withCredentials: true }
                );
            }

            fetchPosts();
            setShowAddPostPopup(false);
            setPostForm({
                title: '',
                author_name: '',
                content: '',
                media_type: 'image',
                media_url: '',
                tag_ids: []
            });
            setMediaFile(null);
        } catch (err) {
            console.error('Error submitting post:', err);
            setFormError(err.response?.data?.error || 'Failed to submit post');
        } finally {
            setSubmitting(false);
        }
    };

    /**
     * Submit new tag creation form
     */
    const handleSubmitTag = async (e) => {
        e.preventDefault();
        
        // Validate tag label (matching database constraints)
        if (!tagForm.label.trim()) {
            setFormError('Tag label is required');
            return;
        }
        if (tagForm.label.length > 50) {
            setFormError('Tag label must be 50 characters or less');
            return;
        }

        setSubmitting(true);
        setFormError('');

        try {
            await axios.post(
                `${hostURL}/api/tags`,
                tagForm,
                { withCredentials: true }
            );
            fetchTags();
            setShowAddTagPopup(false);
            setTagForm({ label: '' });
        } catch (err) {
            console.error('Error creating tag:', err);
            setFormError(err.response?.data?.error || 'Failed to create tag');
        } finally {
            setSubmitting(false);
        }
    };

    /**
     * Handle tag deletion - first step shows confirmation
     */
    const handleDeleteTag = async () => {
        if (!tagToDelete) {
            setFormError('Please select a tag to delete');
            return;
        }

        setSubmitting(true);
        setFormError('');

        try {
            // Delete tag (backend will handle removing post_tags entries first)
            await axios.delete(
                `${hostURL}/api/tags/${tagToDelete}`,
                { withCredentials: true }
            );
            
            // Refresh tags and posts
            fetchTags();
            fetchPosts();
            
            // Close popup and reset state
            setShowDeleteTagPopup(false);
            setShowTagDeleteConfirm(false);
            setTagToDelete('');
        } catch (err) {
            console.error('Error deleting tag:', err);
            setFormError(err.response?.data?.error || 'Failed to delete tag');
        } finally {
            setSubmitting(false);
        }
    };

    /**
     * Open the Read More popup to view full post
     */
    const handleReadMore = (post) => {
        setSelectedPost(post);
        setShowReadMorePopup(true);
        setDropdownOpen(null);
    };

    // ========== Utility Functions ==========
    /**
     * Truncate content to specified length with ellipsis
     */
    const truncateContent = (content, maxLength = 200) => {
        if (content.length <= maxLength) return content;
        return content.substring(0, maxLength) + '...';
    };

    /**
     * Format ISO date string to readable format
     */
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    /**
     * Render media based on post media type
     * Supports: image, video, youtube, none
     * @param {Object} post - Post object with media_type and media_url
     * @param {Boolean} isPreview - If true, renders in preview mode (smaller)
     */
    /**
     * Distribute posts into balanced columns based on estimated heights
     * Number of columns depends on screen width (2 or 3)
     * Posts with media are estimated as taller than text-only posts
     * Memoized to prevent unnecessary recalculations
     */
    const distributedColumns = useMemo(() => {
        
        // Initialize columns
        const columns = Array.from({ length: numColumns }, () => []);
        const columnHeights = Array(numColumns).fill(0);

        posts.forEach(post => {
            // Estimate post height (rough approximation)
            let estimatedHeight = 0;
            
            // Base card structure (title, metadata, tags, button, padding)
            estimatedHeight += 280;
            
            // Media height
            if (post.media_type === 'image' || post.media_type === 'video') {
                estimatedHeight += 192; // var(--media-h) = 192px
            } else if (post.media_type === 'youtube') {
                estimatedHeight += 192;
            }
            
            // Content preview height (truncated to ~150 chars, ~3-4 lines)
            const contentLength = truncateContent(post.content).length;
            estimatedHeight += Math.ceil(contentLength / 50) * 24; // ~24px per line
            
            // Tag height (if more than 3 tags, adds extra line)
            if (post.tags && post.tags.length > 0) {
                estimatedHeight += 32; // Single row of tags
            }
            
            // Gap between cards
            estimatedHeight += 32;

            // Find the shortest column and place post there
            const shortestColumnIndex = columnHeights.indexOf(Math.min(...columnHeights));
            columns[shortestColumnIndex].push(post);
            columnHeights[shortestColumnIndex] += estimatedHeight;
        });

        // Return columns with backward compatibility
        if (numColumns === 2) {
            return { column1: columns[0], column2: columns[1] };
        } else {
            return { column1: columns[0], column2: columns[1], column3: columns[2] };
        }
    }, [posts, numColumns]);

    const renderMedia = (post, isPreview = false) => {
        if (post.media_type === 'none') return null;

        if (post.media_type === 'youtube') {
            return (
                <div className="post-media">
                    <iframe
                        src={post.media_url}
                        title={post.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="post-video"
                    />
                </div>
            );
        }

        if (post.media_type === 'video') {
            // Handle both old filenames and new /api/media/blog/ paths
            const videoSrc = post.media_url.startsWith('/') 
                ? `${hostURL}${post.media_url}` 
                : `${hostURL}/api/media/blog/${post.media_url}`;
            return (
                <div className="post-media">
                    <video controls className="post-video">
                        <source src={videoSrc} type="video/mp4" />
                        Your browser does not support the video tag.
                    </video>
                </div>
            );
        }

        if (post.media_type === 'image') {
            // Handle both old filenames and new /api/media/blog/ paths
            const imageSrc = post.media_url.startsWith('/') 
                ? `${hostURL}${post.media_url}` 
                : `${hostURL}/api/media/blog/${post.media_url}`;
            return (
                <div className="post-media">
                    <img
                        src={imageSrc}
                        alt={post.title}
                        className="post-img"
                    />
                </div>
            );
        }
    };

    return (
        
        <div className="page">
            {/* Hero Section */}
            <section className="blog-hero">
                <div className="hero-container">
                    <div className="hero-text">
                    <h1 className="hero-title">Insights</h1>
                    <p className="hero-subtitle">
                        Industry expertise, practical guidance, and strategic insights from our team.
                    </p>
                    </div>
                </div>
            </section>

            {/* Search Section */}
            <section className="blog-search-section">
                <div className="search-container">
                    <div className="search-input-wrapper">
                    <div className="search-relative">
                        <input
                        type="text"
                        id="blog-search"
                        name="search"
                        placeholder="Search articles..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                        />
                        <Search className="search-icon" size={20} />
                    </div>
                    </div>

                    <div className="tags-container">
                        {topTags.map((tag) => (
                            <button 
                                key={tag.id} 
                                className={`tag-button ${selectedTags.includes(tag.id) ? 'tag-selected' : ''}`}
                                onClick={() => handleTagToggle(tag.id)}
                            >
                                {tag.label}
                            </button>
                        ))}
                        <button 
                            className="tag-button all-tags-button"
                            onClick={() => setShowAllTagsModal(true)}
                        >
                            All Tags...
                        </button>
                        {isAdminOrOwner && (
                            <>
                                <button 
                                    className="tag-add-button"
                                    onClick={() => {
                                        setFormError('');
                                        setShowAddTagPopup(true);
                                    }}
                                >
                                    + Add tag
                                </button>
                                <button 
                                    className="tag-remove-button"
                                    onClick={() => {
                                        setFormError('');
                                        setTagToDelete('');
                                        setShowTagDeleteConfirm(false);
                                        setShowDeleteTagPopup(true);
                                    }}
                                >
                                    - Remove tag
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </section>

            {/* Blog Posts Section */}
            <section className="blog-grid-section content-section">
                <div className="grid-container">
                    {/* Add Post Button Bar (Admin/Owner only) */}
                    {isAdminOrOwner && (
                        <div className="add-post-bar" onClick={handleAddPost}>
                            <Plus size={20} className="add-post-bar-icon" />
                            <span>Add New Post</span>
                        </div>
                    )}

                    {loading ? (
                        <div className="loading-state">Loading posts...</div>
                    ) : error ? (
                        <div className="error-state">{error}</div>
                    ) : posts.length === 0 ? (
                        <div className="empty-state">
                            <p className="empty-text">No articles found matching your search.</p>
                        </div>
                    ) : (() => {
                        const { column1, column2, column3 } = distributedColumns;
                        return (
                            <div className="posts-columns">
                                {/* Column 1 - Balanced distribution */}
                                <div className="posts-column">
                                    {column1.map((post) => (
                                    <article key={post.id} className="post-card" onClick={() => handleReadMore(post)}>
                                        <div className="post-body">
                                            {/* Title displayed above media */}
                                            <h3 className="post-title">{post.title}</h3>
                                        </div>
                                        
                                        {/* Media (image, video, or YouTube) */}
                                        {renderMedia(post, true)}

                                        <div className="post-body">
                                            {/* Truncated content preview */}
                                            <p className="post-excerpt">{truncateContent(post.content)}</p>

                                            <div className="post-meta">
                                                <div className="post-meta-group">
                                                    <User size={16} className="icon-gap" />
                                                    <span>{post.author_name}</span>
                                                </div>
                                                <div className="post-meta-group">
                                                    <Calendar size={16} className="icon-gap" />
                                                    <span>{formatDate(post.posted_at)}</span>
                                                </div>
                                            </div>

                                            <div className="post-footer">
                                                <div className="post-tags">
                                                    {post.tags.slice(0, 3).map(tag => (
                                                        <span key={tag.id} className="post-tag">{tag.label}</span>
                                                    ))}
                                                    {post.tags.length > 3 && (
                                                        <span className="post-tag more-tags">+{post.tags.length - 3} more...</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </article>
                                ))}
                            </div>

                                {/* Column 2 - Balanced distribution */}
                                <div className="posts-column">
                                    {column2.map((post) => (
                                    <article key={post.id} className="post-card" onClick={() => handleReadMore(post)}>
                                        <div className="post-body">
                                            {/* Title displayed above media */}
                                            <h3 className="post-title">{post.title}</h3>
                                        </div>
                                        
                                        {/* Media (image, video, or YouTube) */}
                                        {renderMedia(post, true)}

                                        <div className="post-body">
                                            {/* Truncated content preview */}
                                            <p className="post-excerpt">{truncateContent(post.content)}</p>

                                            <div className="post-meta">
                                                <div className="post-meta-group">
                                                    <User size={16} className="icon-gap" />
                                                    <span>{post.author_name}</span>
                                                </div>
                                                <div className="post-meta-group">
                                                    <Calendar size={16} className="icon-gap" />
                                                    <span>{formatDate(post.posted_at)}</span>
                                                </div>
                                            </div>

                                            <div className="post-footer">
                                                <div className="post-tags">
                                                    {post.tags.slice(0, 3).map(tag => (
                                                        <span key={tag.id} className="post-tag">{tag.label}</span>
                                                    ))}
                                                    {post.tags.length > 3 && (
                                                        <span className="post-tag more-tags">+{post.tags.length - 3} more...</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </article>
                                ))}
                            </div>

                                {/* Column 3 - Balanced distribution (only on large screens) */}
                                {column3 && (
                                    <div className="posts-column">
                                        {column3.map((post) => (
                                        <article key={post.id} className="post-card" onClick={() => handleReadMore(post)}>
                                            <div className="post-body">
                                                {/* Title displayed above media */}
                                                <h3 className="post-title">{post.title}</h3>
                                            </div>
                                            
                                            {/* Media (image, video, or YouTube) */}
                                            {renderMedia(post, true)}

                                            <div className="post-body">
                                                {/* Truncated content preview */}
                                                <p className="post-excerpt">{truncateContent(post.content)}</p>

                                                <div className="post-meta">
                                                    <div className="post-meta-group">
                                                        <User size={16} className="icon-gap" />
                                                        <span>{post.author_name}</span>
                                                    </div>
                                                    <div className="post-meta-group">
                                                        <Calendar size={16} className="icon-gap" />
                                                        <span>{formatDate(post.posted_at)}</span>
                                                    </div>
                                                </div>

                                                <div className="post-footer">
                                                    <div className="post-tags">
                                                        {post.tags.slice(0, 3).map(tag => (
                                                            <span key={tag.id} className="post-tag">{tag.label}</span>
                                                        ))}
                                                        {post.tags.length > 3 && (
                                                            <span className="post-tag more-tags">+{post.tags.length - 3} more...</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </article>
                                    ))}
                                </div>
                                )}
                            </div>
                        );
                    })()}
                </div>
            </section>

            {/* Add/Edit Post Popup */}
            {showAddPostPopup && (
                <Portal>
                <div className="popup-overlay">
                    <div className="popup-content blog-popup" onClick={(e) => e.stopPropagation()}>
                        <h2>{isEditMode ? 'Edit Post' : 'Add New Post'}</h2>
                        
                        {formError && <div className="error-message">{formError}</div>}

                        <form onSubmit={handleSubmitPost}>
                            <div className="form-group">
                                <label>Author Name *</label>
                                <input
                                    type="text"
                                    name="author_name"
                                    value={postForm.author_name}
                                    onChange={handlePostFormChange}
                                    placeholder="Enter author name"
                                    required
                                />
                                <div className={`char-count ${(postForm.author_name?.length || 0) > 100 ? 'error' : (postForm.author_name?.length || 0) > 90 ? 'warning' : ''}`}>
                                    {postForm.author_name?.length || 0} / 100
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Title *</label>
                                <input
                                    type="text"
                                    name="title"
                                    value={postForm.title}
                                    onChange={handlePostFormChange}
                                    placeholder="Enter post title"
                                    required
                                />
                                <div className={`char-count ${(postForm.title?.length || 0) > 255 ? 'error' : (postForm.title?.length || 0) > 229 ? 'warning' : ''}`}>
                                    {postForm.title?.length || 0} / 255
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Content *</label>
                                <textarea
                                    name="content"
                                    value={postForm.content}
                                    onChange={handlePostFormChange}
                                    placeholder="Enter post content"
                                    rows="8"
                                    required
                                />
                                <div className={`char-count ${(postForm.content?.length || 0) > 10000 ? 'error' : (postForm.content?.length || 0) > 9000 ? 'warning' : ''}`}>
                                    {postForm.content?.length || 0} / 10,000
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Media Type *</label>
                                <select
                                    name="media_type"
                                    value={postForm.media_type}
                                    onChange={handlePostFormChange}
                                >
                                    <option value="image">Image</option>
                                    <option value="youtube">YouTube Video</option>
                                    <option value="none">None</option>
                                </select>
                            </div>

                            {postForm.media_type === 'youtube' ? (
                                <div className="form-group">
                                    <label>YouTube URL *</label>
                                    <input
                                        type="url"
                                        name="media_url"
                                        value={postForm.media_url}
                                        onChange={handlePostFormChange}
                                        placeholder="https://youtube.com/watch?v=... or https://youtu.be/..."
                                        required
                                    />
                                    <div className={`char-count ${(postForm.media_url?.length || 0) > 2048 ? 'error' : (postForm.media_url?.length || 0) > 1843 ? 'warning' : ''}`}>
                                        {postForm.media_url?.length || 0} / 2,048
                                    </div>
                                </div>
                            ) : postForm.media_type === 'image' ? (
                                <div className="form-group">
                                    <label>
                                        Upload Image
                                        {!isEditMode && ' *'}
                                    </label>
                                    <div 
                                        className={`file-drop-zone ${isDragging ? 'dragging' : ''}`}
                                        onDragEnter={handleDragEnter}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                    >
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleMediaFileChange}
                                            id="image-upload"
                                            style={{ display: 'none' }}
                                        />
                                        <label htmlFor="image-upload" className="file-drop-label">
                                            {mediaFile ? (
                                                <span className="file-selected">✓ {mediaFile.name}</span>
                                            ) : (
                                                <>
                                                    <span className="drop-text">Drag & drop an image here</span>
                                                    <span className="drop-or"> or </span>
                                                    <span className="drop-browse">Click to browse</span>
                                                </>
                                            )}
                                        </label>
                                    </div>
                                    {isEditMode && postForm.media_url && !mediaFile && (
                                        <p className="file-note">Current file will be kept if no new file is uploaded</p>
                                    )}
                                </div>
                            ) : null}

                            <div className="form-group">
                                <label>Tags</label>
                                <div className="tags-checkboxes">
                                    {tags.map(tag => (
                                        <label key={tag.id} className="checkbox-label">
                                            <input
                                                type="checkbox"
                                                checked={postForm.tag_ids.includes(tag.id)}
                                                onChange={() => handleTagCheckbox(tag.id)}
                                            />
                                            {tag.label}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="form-actions">
                                <button 
                                    type="button" 
                                    className="cancel-button"
                                    onClick={() => setShowAddPostPopup(false)}
                                    disabled={submitting}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit" 
                                    className="submit-button"
                                    disabled={submitting}
                                >
                                    {submitting ? 'Submitting...' : (isEditMode ? 'Update Post' : 'Create Post')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
                </Portal>
            )}

            {/* Add Tag Popup */}
            {showAddTagPopup && (
                <Portal>
                <div className="popup-overlay">
                    <div className="popup-content small-popup" onClick={(e) => e.stopPropagation()}>
                        <h2>Add New Tag</h2>
                        
                        {formError && <div className="error-message">{formError}</div>}

                        <form onSubmit={handleSubmitTag}>
                            <div className="form-group">
                                <label>Tag Label *</label>
                                <input
                                    type="text"
                                    value={tagForm.label}
                                    onChange={(e) => setTagForm({ label: e.target.value })}
                                    placeholder="Enter tag label"
                                    required
                                />
                                <div className={`char-count ${(tagForm.label?.length || 0) > 50 ? 'error' : (tagForm.label?.length || 0) > 45 ? 'warning' : ''}`}>
                                    {tagForm.label?.length || 0} / 50
                                </div>
                            </div>

                            <div className="form-actions">
                                <button 
                                    type="button" 
                                    className="cancel-button"
                                    onClick={() => setShowAddTagPopup(false)}
                                    disabled={submitting}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit" 
                                    className="submit-button"
                                    disabled={submitting}
                                >
                                    {submitting ? 'Creating...' : 'Create Tag'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
                </Portal>
            )}

            {/* Delete Tag Popup */}
            {showDeleteTagPopup && (
                <Portal>
                <div className="popup-overlay">
                    <div className="popup-content small-popup" onClick={(e) => e.stopPropagation()}>
                        <h2>Remove Tag</h2>
                        
                        {formError && <div className="error-message">{formError}</div>}

                        {!showTagDeleteConfirm ? (
                            <>
                                <div className="form-group">
                                    <label>Select Tag to Delete *</label>
                                    <select
                                        value={tagToDelete}
                                        onChange={(e) => setTagToDelete(e.target.value)}
                                        required
                                    >
                                        <option value="">Choose a tag...</option>
                                        {tags.map(tag => (
                                            <option key={tag.id} value={tag.id}>
                                                {tag.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-actions">
                                    <button 
                                        type="button" 
                                        className="cancel-button"
                                        onClick={() => {
                                            setShowDeleteTagPopup(false);
                                            setTagToDelete('');
                                        }}
                                        disabled={submitting}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="button" 
                                        className="delete-button"
                                        onClick={() => {
                                            if (!tagToDelete) {
                                                setFormError('Please select a tag to delete');
                                                return;
                                            }
                                            setFormError('');
                                            setShowTagDeleteConfirm(true);
                                        }}
                                        disabled={submitting || !tagToDelete}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="warning-message">
                                    <p><strong>Are you sure?</strong></p>
                                    <p>This will remove all instances of this tag from posts. This action cannot be undone.</p>
                                </div>

                                <div className="form-actions">
                                    <button 
                                        type="button" 
                                        className="cancel-button"
                                        onClick={() => setShowTagDeleteConfirm(false)}
                                        disabled={submitting}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="button" 
                                        className="delete-button"
                                        onClick={handleDeleteTag}
                                        disabled={submitting}
                                    >
                                        {submitting ? 'Deleting...' : 'Confirm Delete'}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                </Portal>
            )}

            {/* Read More Popup */}
            {showReadMorePopup && selectedPost && (
                <Portal>
                <div className="popup-overlay">
                    <div className="popup-content read-more-popup" onClick={(e) => e.stopPropagation()}>
                        <div className="popup-header">
                            <button 
                                className="close-button"
                                onClick={() => setShowReadMorePopup(false)}
                            >
                                <X size={24} />
                            </button>
                            {isAdminOrOwner && (
                                <div className="post-menu">
                                    <button 
                                        className="menu-button"
                                        onClick={() => setDropdownOpen(dropdownOpen === selectedPost.id ? null : selectedPost.id)}
                                    >
                                        <MoreVertical size={20} />
                                    </button>
                                    {dropdownOpen === selectedPost.id && (
                                        <div className="dropdown-menu">
                                            <button onClick={() => handleEditPost(selectedPost)}>Edit</button>
                                            <button 
                                                className="delete-option"
                                                onClick={() => {
                                                    setPostToDelete(selectedPost);
                                                    setShowDeleteConfirm(true);
                                                }}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="popup-body">
                            {renderMedia(selectedPost, false)}
                            
                            <div className="post-author-date">
                                <span>{selectedPost.author_name}</span>
                                <span className="separator">•</span>
                                <span>Updated: {formatDate(selectedPost.updated_at)}</span>
                            </div>

                            <h1 className="post-full-title">{selectedPost.title}</h1>
                            
                            <div className="post-full-tags">
                                {selectedPost.tags.map(tag => (
                                    <span key={tag.id} className="post-tag">{tag.label}</span>
                                ))}
                            </div>

                            <div className="post-full-content">
                                {selectedPost.content}
                            </div>
                        </div>
                    </div>
                </div>
                </Portal>
            )}

            {/* Delete Confirmation Popup */}
            {showDeleteConfirm && postToDelete && (
                <Portal>
                <div className="popup-overlay">
                    <div className="popup-content small-popup" onClick={(e) => e.stopPropagation()}>
                        <h2>Delete Post</h2>
                        <p>Are you sure you want to delete "{postToDelete.title}"? This action cannot be undone.</p>
                        
                        {formError && <div className="error-message">{formError}</div>}

                        <div className="form-actions">
                            <button 
                                className="cancel-button"
                                onClick={() => setShowDeleteConfirm(false)}
                            >
                                Cancel
                            </button>
                            <button 
                                className="delete-button"
                                onClick={handleDeletePost}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
                </Portal>
            )}

            {/* All Tags Modal */}
            {showAllTagsModal && (
                <Portal>
                <div className="popup-overlay">
                    <div className="popup-content all-tags-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="popup-header">
                            <h2>All Tags</h2>
                            <button 
                                className="close-button"
                                onClick={() => setShowAllTagsModal(false)}
                            >
                                <X size={24} />
                            </button>
                        </div>
                        
                        <div className="popup-body">
                            <p className="modal-description">Select or deselect tags to filter posts</p>
                            <div className="all-tags-grid">
                                {tags
                                    .sort((a, b) => (b.post_count || 0) - (a.post_count || 0))
                                    .map((tag) => (
                                    <label key={tag.id} className="tag-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={selectedTags.includes(tag.id)}
                                            onChange={() => handleTagToggle(tag.id)}
                                        />
                                        <span className="tag-checkbox-text">
                                            {tag.label}
                                            {tag.post_count > 0 && (
                                                <span className="tag-post-count">({tag.post_count})</span>
                                            )}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div className="form-actions">
                            <button 
                                className="cancel-button"
                                onClick={() => setShowAllTagsModal(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
                </Portal>
            )}

        </div>
    );
}

export default Blog;